import { useEffect, useRef, useState } from "react";
import { posApi } from "../../../api/pos";
import { salesInvoiceApi } from "../../../api/salesInvoice";
import { unwrapResponse } from "../../../api/client";

interface TenderModalProps {
  onClose: () => void;
  onPaymentComplete: (
    paymentMethods: Array<{ method: string; amount: number }>,
    payments: TenderLine[],
    creditMemo?: { memoId: number; amount: number } | null,
  ) => Promise<void>;
  grandTotal: number;
  customerId?: number | null;
  exchangeCreditMemo?: { memoId: number; amount: number } | null;
}

interface CreditMemo {
  id: number;
  invoiceNumber: string;
  dueAmount: number;
  grandTotal: number;
  date?: string;
}

interface TenderMethod {
  id: string;
  name: string;
  icon: string;
  color: string;
  methodId?: number | null;
  accountId?: number | null;
  paymentMethodTypeId?: number | null;
  expenseAccountId?: number | null;
  charge?: number;
  chargeType?: any;
}

interface TenderLine {
  id: string;
  method: string;
  methodName: string;
  amountStr: string;
  amount: number;
  icon: string;
  color: string;
  methodId?: number | null;
  accountId?: number | null;
  paymentMethodTypeId?: number | null;
  expenseAccountId?: number | null;
  charge?: number;
  chargeType?: any;
  transactionId: string;
}

export default function TenderModal({
  onClose,
  onPaymentComplete,
  grandTotal,
  customerId,
  exchangeCreditMemo,
}: TenderModalProps) {
  const [tenderLines, setTenderLines] = useState<TenderLine[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoadingMethods, setIsLoadingMethods] = useState(false);
  const [methodsLoaded, setMethodsLoaded] = useState(false);
  const [tenderError, setTenderError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const lastAddedIdRef = useRef<string | null>(null);
  const cashInitializedRef = useRef(false);
  const [showCreditMemo, setShowCreditMemo] = useState(false);
  const [creditMemos, setCreditMemos] = useState<CreditMemo[]>([]);
  const [appliedCreditMemoId, setAppliedCreditMemoId] = useState<number | null>(
    null,
  );
  const [appliedCreditAmount, setAppliedCreditAmount] = useState(0);

  const totalDue = Math.round(grandTotal);
  const totalPaid = tenderLines.reduce((sum, line) => sum + line.amount, 0);
  const remainingBalance = Math.max(
    0,
    totalDue - appliedCreditAmount - totalPaid,
  );
  const changeDue =
    totalPaid + appliedCreditAmount > totalDue
      ? totalPaid + appliedCreditAmount - totalDue
      : 0;

  const [tenderMethods, setTenderMethods] = useState<TenderMethod[]>([
    {
      id: "cash",
      name: "Cash",
      icon: "ri-money-dollar-circle-line",
      color: "bg-green-500",
      paymentMethodTypeId: 0,
    },
    {
      id: "card",
      name: "Card (EMV)",
      icon: "ri-bank-card-line",
      color: "bg-blue-500",
      paymentMethodTypeId: 1,
    },
    {
      id: "mobile",
      name: "Mobile Wallet",
      icon: "ri-smartphone-line",
      color: "bg-purple-500",
      paymentMethodTypeId: 2,
    },
  ]);

  useEffect(() => {
    const loadTenderMethods = async () => {
      const outletRaw = localStorage.getItem("outlet");
      if (!outletRaw) return;

      let outletId = 0;
      try {
        outletId = Number(JSON.parse(outletRaw)?.id ?? 0);
      } catch {
        outletId = 0;
      }
      if (!outletId) return;

      setIsLoadingMethods(true);
      setTenderError(null);
      try {
        const outletDetailsRes = await posApi.getOutletById(outletId);
        const outletDetails = unwrapResponse<any>(outletDetailsRes);
        const methods: TenderMethod[] = [];

        if (outletDetails?.isCashMethodActive && outletDetails?.cashMethodId) {
          methods.push({
            id: `cash-${outletDetails.cashMethodId}`,
            name: outletDetails?.cashMethodName ?? "Cash",
            icon: "ri-money-dollar-circle-line",
            color: "bg-green-500",
            methodId: Number(outletDetails?.cashMethodId),
            accountId: Number(outletDetails?.cashAccountId ?? 0) || null,
            paymentMethodTypeId: 0,
          });
        }

        const outletPaymentMethods = Array.isArray(
          outletDetails?.outletPaymentMethods,
        )
          ? outletDetails.outletPaymentMethods
          : [];

        outletPaymentMethods.forEach((method: any) => {
          if (!method?.isActive) return;
          const typeId = Number(
            method?.paymentMethodType?.id ?? method?.paymentMethodTypeId ?? -1,
          );
          methods.push({
            id: `${typeId}-${method?.paymentMethod?.id ?? method?.methodId}`,
            name:
              method?.paymentMethod?.name ?? method?.methodName ?? "Payment",
            icon:
              typeId === 1
                ? "ri-bank-card-line"
                : typeId === 2
                  ? "ri-smartphone-line"
                  : "ri-bank-card-line",
            color:
              typeId === 1
                ? "bg-blue-500"
                : typeId === 2
                  ? "bg-purple-500"
                  : "bg-indigo-500",
            methodId:
              Number(method?.paymentMethod?.id ?? method?.methodId ?? 0) ||
              null,
            accountId:
              Number(method?.account?.id ?? method?.accountId ?? 0) || null,
            paymentMethodTypeId: typeId >= 0 ? typeId : null,
            expenseAccountId:
              Number(
                method?.expenseAccount?.id ?? method?.expenseAccountId ?? 0,
              ) || null,
            charge: Number(method?.bankCharge ?? method?.charge ?? 0),
            chargeType: method?.bankChargeType ?? method?.chargeType ?? null,
          });
        });

        if (methods.length > 0) {
          setTenderMethods(methods);
          if (!cashInitializedRef.current) {
            cashInitializedRef.current = true;
            const defaultMethod =
              methods.find((m) => m.paymentMethodTypeId === 0) ?? methods[0];
            const newId = Date.now().toString();
            lastAddedIdRef.current = newId;
            setTenderLines([
              {
                id: newId,
                method: defaultMethod.id,
                methodName: defaultMethod.name,
                amountStr: "",
                amount: 0,
                icon: defaultMethod.icon,
                color: defaultMethod.color,
                methodId: defaultMethod.methodId ?? null,
                accountId: defaultMethod.accountId ?? null,
                paymentMethodTypeId: defaultMethod.paymentMethodTypeId ?? null,
                expenseAccountId: defaultMethod.expenseAccountId ?? null,
                charge: defaultMethod.charge ?? 0,
                chargeType: defaultMethod.chargeType ?? null,
                transactionId: "",
              },
            ]);
          }
        }
      } catch {
        setTenderError(
          "Failed to load outlet payment methods. Using defaults.",
        );
      } finally {
        setIsLoadingMethods(false);
        setMethodsLoaded(true);
      }
    };

    loadTenderMethods();
  }, []);

  useEffect(() => {
    if (!customerId) return;
    const fetchCreditMemos = async () => {
      try {
        const payload = {
          customerId,
          customerGroupId: 0,
          referenceId: null,
          invoiceTypeIds: [1],
          referenceTypeIds: [8],
          invoiceStatusId: [1, 2],
          startDate: null,
          invoiceIds: [],
          endDate: null,
          isDateAscending: false,
        };
        const res = await salesInvoiceApi.getCreditMemos(payload);
        const data = unwrapResponse<any>(res);
        const list: CreditMemo[] = (Array.isArray(data) ? data : [])
          .map((item: any) => ({
            id: item.id,
            invoiceNumber: item.invoiceNumber ?? `CM-${item.id}`,
            dueAmount: Number(item.dueAmount ?? item.grandTotal ?? 0),
            grandTotal: Number(item.grandTotal ?? 0),
            date: item.date,
          }))
          .filter((m: CreditMemo) => m.dueAmount > 0);
        setCreditMemos(list);
      } catch {
        /* silently fail */
      }
    };
    fetchCreditMemos();
  }, [customerId]);

  // Auto-apply exchange credit memo when provided
  useEffect(() => {
    if (!exchangeCreditMemo) return;
    if (
      appliedCreditMemoId !== null &&
      appliedCreditMemoId !== exchangeCreditMemo.memoId
    )
      return;
    const memo = creditMemos.find((m) => m.id === exchangeCreditMemo.memoId);
    const available = memo ? memo.dueAmount : exchangeCreditMemo.amount;
    setAppliedCreditMemoId(exchangeCreditMemo.memoId);
    setAppliedCreditAmount(
      Math.min(available, exchangeCreditMemo.amount, totalDue),
    );
    setShowCreditMemo(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exchangeCreditMemo, creditMemos, totalDue]);

  // Barcode scan handler — matches credit memo invoice number, only when credit memo panel is open
  useEffect(() => {
    if (!showCreditMemo) return;
    let buffer = '';
    let lastKeyTime = 0;
    const onKeyDown = (e: KeyboardEvent) => {
      const tag = (document.activeElement as HTMLElement)?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      const now = Date.now();
      if (e.key === 'Enter') {
        const scanned = buffer.trim();
        buffer = '';
        if (!scanned) return;
        const memo = creditMemos.find(
          (m) => m.invoiceNumber?.toLowerCase() === scanned.toLowerCase(),
        );
        if (memo) {
          const applyAmount = Math.min(memo.dueAmount, totalDue);
          setShowCreditMemo(true);
          setAppliedCreditMemoId(memo.id);
          setAppliedCreditAmount(applyAmount);
          if (totalDue - applyAmount <= 0.01) setTenderLines([]);
        }
      } else if (e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
        if (now - lastKeyTime > 300 && buffer.length > 0) buffer = '';
        buffer += e.key;
        lastKeyTime = now;
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [creditMemos, totalDue, showCreditMemo]);

  const totalCreditApplied = appliedCreditAmount;
  const effectiveTotalDue = Math.max(0, totalDue - totalCreditApplied);

  const handleApplyCreditMemo = (memo: CreditMemo) => {
    if (appliedCreditMemoId === memo.id) {
      setAppliedCreditMemoId(null);
      setAppliedCreditAmount(0);
    } else {
      const applyAmount = Math.min(memo.dueAmount, totalDue);
      setAppliedCreditMemoId(memo.id);
      setAppliedCreditAmount(applyAmount);
      if (totalDue - applyAmount <= 0.01) setTenderLines([]);
    }
  };

  const handleSelectTender = (methodId: string) => {
    const method = tenderMethods.find((m) => m.id === methodId);
    if (!method) return;
    if (tenderLines.some((line) => line.method === methodId)) return;

    const newId = Date.now().toString();
    lastAddedIdRef.current = newId;

    const newLine: TenderLine = {
      id: newId,
      method: methodId,
      methodName: method.name,
      amountStr: "",
      amount: 0,
      icon: method.icon,
      color: method.color,
      methodId: method.methodId ?? null,
      accountId: method.accountId ?? null,
      paymentMethodTypeId: method.paymentMethodTypeId ?? null,
      expenseAccountId: method.expenseAccountId ?? null,
      charge: method.charge ?? 0,
      chargeType: method.chargeType ?? null,
      transactionId: "",
    };

    setTenderLines((prev) => [...prev, newLine]);
    setTenderError(null);
  };

  const handleUpdateLineAmount = (lineId: string, value: string) => {
    setTenderLines((prev) =>
      prev.map((line) =>
        line.id === lineId
          ? {
              ...line,
              amountStr: value,
              amount: parseFloat(value) > 0 ? parseFloat(value) : 0,
            }
          : line,
      ),
    );
  };

  const handleUpdateLineTransactionId = (lineId: string, value: string) => {
    setTenderLines((prev) =>
      prev.map((line) =>
        line.id === lineId ? { ...line, transactionId: value } : line,
      ),
    );
  };

  const handleRemoveLine = (lineId: string) => {
    setTenderLines((prev) => prev.filter((line) => line.id !== lineId));
  };

  const allAmountsValid = tenderLines.every((line) => line.amount > 0);
  const netAmount = Math.max(0, totalDue - appliedCreditAmount);
  const nonCashTotal = tenderLines
    .filter((line) => line.paymentMethodTypeId !== 0)
    .reduce((sum, line) => sum + line.amount, 0);
  const nonCashExceedsNet = nonCashTotal > netAmount + 0.001;
  const appliedMemo = appliedCreditMemoId
    ? creditMemos.find((m) => m.id === appliedCreditMemoId)
    : null;
  const creditMemoFitsOrder = !appliedMemo || appliedMemo.dueAmount <= grandTotal;
  const canFinalize =
    Math.abs(remainingBalance) < 0.01 &&
    (tenderLines.length > 0 || appliedCreditAmount > 0) &&
    (tenderLines.length === 0 || allAmountsValid) &&
    !nonCashExceedsNet &&
    creditMemoFitsOrder;

  const handleCompletePayment = async () => {
    if (!canFinalize) return;

    setIsProcessing(true);
    setTenderError(null);

    try {
      const paymentSummary = tenderLines.map((line) => ({
        method: line.methodName,
        amount: line.amount,
      }));
      const activeCreditMemo = appliedCreditMemoId
        ? { memoId: appliedCreditMemoId, amount: appliedCreditAmount }
        : null;
      await onPaymentComplete(paymentSummary, tenderLines, activeCreditMemo);

      setIsProcessing(false);
      setShowSuccess(true);

      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err: any) {
      setIsProcessing(false);
      setTenderError(err?.message || "Failed to complete payment.");
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter" && canFinalize && !isProcessing) {
        handleCompletePayment();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canFinalize, isProcessing]);

  if (showSuccess) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-8 text-center">
          <div className="w-20 h-20 flex items-center justify-center bg-green-500 text-white rounded-full mx-auto mb-4">
            <i className="ri-check-line text-5xl"></i>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2 whitespace-nowrap">
            Payment Successful!
          </h2>
          <p className="text-gray-600 mb-4 whitespace-nowrap">
            Transaction completed successfully
          </p>
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-1 whitespace-nowrap">
              Total Paid
            </p>
            <p className="text-3xl font-bold text-gray-900 whitespace-nowrap">
              {totalPaid.toFixed(2)}
            </p>
            {changeDue > 0 && (
              <>
                <p className="text-sm text-gray-600 mt-3 mb-1 whitespace-nowrap">
                  Change Due
                </p>
                <p className="text-2xl font-bold text-green-600 whitespace-nowrap">
                  {changeDue.toFixed(2)}
                </p>
              </>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-4 whitespace-nowrap">
            Opening print dialog...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] min-h-[300px] overflow-hidden flex flex-col">
        <div className="bg-teal-600 text-white px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold whitespace-nowrap">
              Split Payment Tender
            </h2>
            <p className="text-sm text-teal-100 whitespace-nowrap">
              Select a payment method to add it to the summary
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="w-8 h-8 flex items-center justify-center hover:bg-teal-700 rounded-lg transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <div className="flex-1 overflow-hidden flex">
          {/* Left Panel - Method Selection */}
          <div className="w-96 p-4 border-r border-gray-200 flex flex-col overflow-y-auto shrink-0">
            {/* Amount Display */}
            <div className="mb-4 grid grid-cols-2 gap-3">
              <div className="p-3 bg-gray-900 text-white rounded-xl text-center">
                <p className="text-xs text-gray-400 mb-1 whitespace-nowrap">
                  Total Due
                </p>
                <p className="text-2xl font-bold whitespace-nowrap">
                  {totalDue.toFixed(2)}
                </p>
              </div>
              <div
                className={`p-3 rounded-xl text-center ${remainingBalance > 0 ? "bg-orange-500" : "bg-green-500"} text-white`}
              >
                <p className="text-xs text-white/80 mb-1 whitespace-nowrap">
                  Remaining
                </p>
                <p className="text-2xl font-bold whitespace-nowrap">
                  {remainingBalance.toFixed(2)}
                </p>
              </div>
            </div>

            {/* Payment Method Selection */}
            <div>
              <h3 className="text-xs font-bold text-gray-900 mb-2 whitespace-nowrap">
                Select Payment Method
              </h3>
              {isLoadingMethods && (
                <div className="mb-2 text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded px-3 py-1.5">
                  Loading outlet payment methods...
                </div>
              )}
              {tenderError && (
                <div className="mb-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-1.5">
                  {tenderError}
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                {tenderMethods.map((method) => (
                  <button
                    key={method.id}
                    onClick={() => handleSelectTender(method.id)}
                    disabled={
                      isProcessing ||
                      isLoadingMethods ||
                      remainingBalance <= 0 ||
                      tenderLines.some((line) => line.method === method.id)
                    }
                    className="p-2.5 rounded-lg border-2 border-gray-200 hover:border-teal-400 hover:bg-teal-50 bg-white transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <div
                      className={`w-8 h-8 flex items-center justify-center ${method.color} text-white rounded-lg shrink-0`}
                    >
                      <i className={`${method.icon} text-base`}></i>
                    </div>
                    <p className="text-xs font-semibold text-gray-900 text-left leading-tight">
                      {method.name}
                    </p>
                  </button>
                ))}
              </div>
            </div>

            {tenderLines.length === 0 && (
              <div className="flex-1 flex items-center justify-center text-gray-400 mt-4">
                <div className="text-center">
                  <i className="ri-hand-coin-line text-4xl mb-2"></i>
                  <p className="text-xs whitespace-nowrap">
                    Select a payment method to begin
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Right Panel - Payment Summary with inline inputs */}
          <div className="flex-1 bg-gray-50 p-6 flex flex-col">
            <h3 className="text-sm font-bold text-gray-900 mb-4 whitespace-nowrap">
              Payment Summary
            </h3>

            <div className="flex-1 overflow-y-auto">
              {tenderLines.length === 0 ? (
                <div className="flex items-center justify-center text-gray-400 py-10">
                  <div className="text-center">
                    <i className="ri-file-list-3-line text-6xl mb-3"></i>
                    <p className="text-sm whitespace-nowrap">
                      No payments added yet
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-2 mb-4">
                  {tenderLines.map((line, index) => (
                    <div
                      key={line.id}
                      className="bg-white rounded-lg px-3 py-2.5 border border-gray-200"
                    >
                      {/* Name + inputs side by side */}
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-7 h-7 flex items-center justify-center ${line.color} text-white rounded-lg shrink-0`}
                        >
                          <i className={`${line.icon} text-sm`}></i>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs text-gray-500 whitespace-nowrap">
                            {line.methodName}
                          </p>
                        </div>
                        {(line.paymentMethodTypeId === 1 ||
                          line.paymentMethodTypeId === 2) && (
                          <input
                            type="text"
                            value={line.transactionId}
                            onChange={(e) =>
                              handleUpdateLineTransactionId(
                                line.id,
                                e.target.value,
                              )
                            }
                            placeholder="Transaction ID"
                            disabled={isProcessing}
                            className="w-52 h-8 px-2 border-2 border-gray-300 rounded-lg text-xs focus:outline-none focus:border-teal-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                          />
                        )}
                        <input
                          type="number"
                          onWheel={(e) => e.currentTarget.blur()}
                          value={line.amountStr}
                          onChange={(e) =>
                            handleUpdateLineAmount(line.id, e.target.value)
                          }
                          placeholder="0.00"
                          disabled={isProcessing}
                          step="0.01"
                          autoFocus={line.id === lastAddedIdRef.current}
                          className={`w-40 h-8 px-2 border-2 rounded-lg text-sm font-semibold text-right focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed ${
                            nonCashExceedsNet && line.paymentMethodTypeId !== 0
                              ? "border-red-400 focus:border-red-500"
                              : "border-gray-300 focus:border-teal-500"
                          }`}
                        />
                        <button
                          onClick={() => handleRemoveLine(line.id)}
                          disabled={isProcessing}
                          className="w-6 h-6 flex items-center justify-center hover:bg-red-50 rounded cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                          title="Remove"
                        >
                          <i className="ri-delete-bin-line text-red-500 text-sm"></i>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Credit Memos */}
              {creditMemos.length > 0 && (
                <div className="mt-3 mb-3">
                  {!showCreditMemo ? (
                    <button
                      onClick={() => setShowCreditMemo(true)}
                      disabled={isProcessing}
                      className="w-full flex items-center justify-center gap-2 px-3 py-2 border-2 border-teal-300 text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-lg text-xs font-semibold transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <i className="ri-exchange-line text-base"></i>
                      Make Exchange
                    </button>
                  ) : (
                    <>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-bold text-gray-700 whitespace-nowrap">
                          Credit Memo
                        </p>
                        <button
                          onClick={() => {
                            setShowCreditMemo(false);
                            setAppliedCreditMemoId(null);
                            setAppliedCreditAmount(0);
                          }}
                          className="text-xs text-gray-400 hover:text-gray-600 cursor-pointer"
                        >
                          <i className="ri-close-line"></i>
                        </button>
                      </div>
                      <select
                        value={appliedCreditMemoId ?? ""}
                        onChange={(e) => {
                          const id = Number(e.target.value);
                          if (!id) {
                            setAppliedCreditMemoId(null);
                            setAppliedCreditAmount(0);
                          } else {
                            const memo = creditMemos.find((m) => m.id === id);
                            if (memo) handleApplyCreditMemo(memo);
                          }
                        }}
                        disabled={isProcessing}
                        className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:border-teal-500 disabled:opacity-50 disabled:cursor-not-allowed bg-white cursor-pointer"
                      >
                        <option value="">— Select credit memo —</option>
                        {creditMemos.map((memo) => (
                          <option key={memo.id} value={memo.id}>
                            {memo.invoiceNumber} (Available:{" "}
                            {memo.dueAmount.toFixed(2)})
                          </option>
                        ))}
                      </select>
                      {appliedCreditAmount > 0 && (
                        <p className="text-xs font-semibold text-teal-700 mt-1.5 whitespace-nowrap">
                          Applied: {appliedCreditAmount.toFixed(2)}
                        </p>
                      )}
                      {appliedMemo && appliedMemo.dueAmount > grandTotal && (
                        <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1.5 mt-1.5">
                          Credit memo value ({appliedMemo.dueAmount.toFixed(2)}) exceeds order total. You must spend at least {appliedMemo.dueAmount.toFixed(2)} to use this credit memo.
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Non-cash overage warning */}
              {nonCashExceedsNet && (
                <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1.5 mb-2">
                  Non-cash payments cannot exceed the net amount (
                  {netAmount.toFixed(2)}). Only cash can exceed for change.
                </p>
              )}

              {/* Summary Totals */}
              <div className="space-y-3 pt-4 border-t-2 border-gray-300">
                {appliedCreditAmount > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-teal-700 whitespace-nowrap">
                      Credit Applied
                    </span>
                    <span className="text-sm font-bold text-teal-700 whitespace-nowrap">
                      -{appliedCreditAmount.toFixed(2)}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 whitespace-nowrap">
                    Total Paid
                  </span>
                  <span className="text-lg font-bold text-gray-900 whitespace-nowrap">
                    {(totalPaid + appliedCreditAmount).toFixed(2)}
                  </span>
                </div>
                {changeDue > 0 && (
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                    <span className="text-sm font-semibold text-green-700 whitespace-nowrap">
                      Change Due
                    </span>
                    <span className="text-xl font-bold text-green-700 whitespace-nowrap">
                      {changeDue.toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
            </div>
            {/* end scrollable area */}
          </div>
        </div>

        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex items-center justify-between">
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="px-6 py-3 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg font-semibold whitespace-nowrap transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>

          <button
            onClick={handleCompletePayment}
            disabled={!canFinalize || isProcessing}
            className={`px-8 py-3 rounded-lg font-bold whitespace-nowrap transition-colors ${
              !canFinalize || isProcessing
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-teal-600 hover:bg-teal-700 text-white cursor-pointer"
            }`}
          >
            {isProcessing ? (
              <>
                <i className="ri-loader-4-line mr-2 animate-spin"></i>
                Processing...
              </>
            ) : (
              <>
                <i className="ri-check-line mr-2"></i>
                Complete Sale
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
